/**
 * Deterministic time helpers. All timestamps crossing a boundary are UTC
 * ISO-8601 with `Z` and millisecond precision.
 */

const ISO_UTC_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;

/** Injected clock. Nothing calls `Date.now()` directly outside an adapter. */
export type Clock = () => number;

/** The production clock. The only place `Date.now` is allowed. */
export const systemClock: Clock = () => Date.now();

/** Formats epoch milliseconds as a canonical UTC ISO-8601 string. */
export function toIsoUtc(epochMs: number): string {
  return new Date(epochMs).toISOString();
}

/** Type guard for canonical UTC ISO-8601 timestamps. */
export function isIsoUtc(value: string): boolean {
  if (!ISO_UTC_PATTERN.test(value)) return false;
  return !Number.isNaN(Date.parse(value));
}

/** Parses a UTC ISO-8601 timestamp into epoch milliseconds. */
export function parseIsoUtc(value: string): number {
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    throw new TypeError(`Invalid UTC ISO-8601 timestamp: ${value}`);
  }
  return parsed;
}

/** Adds whole seconds to an epoch-millisecond instant. */
export function addSeconds(epochMs: number, seconds: number): number {
  return epochMs + seconds * 1000;
}

/** Whole seconds between two instants (`to - from`), floored at zero. */
export function secondsBetween(fromMs: number, toMs: number): number {
  return Math.max(0, Math.floor((toMs - fromMs) / 1000));
}
